// A mastermind's recordings: every session of their group, tagged, so they can find the session
// where somebody had their problem (Accelerator.dc.html's Recordings; design 5.3's "grouped by month
// and group for the Accelerator"). Pure: no network, no React, so a test can import it.
//
// - The rows are the recordings released to them (recording_content comes back only once a
//   recording is released, and only their group's and every group's: row level security), newest
//   first, each with its date tile, title ("Group 2, session 2"), length and tags.
// - Search finds the words in the title, a tag or a chapter; a topic keeps the recordings tagged
//   with it. The tags that match are marked.
// - The topic chips are the tags on those recordings, most used first and then by name (the rule
//   of hub.recording_topics), the first TOPIC_CHIPS of them.
// - The words name their group ("Every Group 2 session, tagged, ..."), or none for a recording
//   list with no group (a member in no group, or a mastermind with 1 for every group).
import * as f from '../lib/format.js'
import { myIntake, progressFor } from './model.js'
import { anyThumbnail, cardThumb } from './recordingThumbs.js'

export const TOPIC_CHIPS = 8
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// Their group's name, or null
export function groupOf(data) {
  return myIntake(data)?.name || null
}

// The board's words, with their group
export const taggedLine = group => `Every ${group ? `${group} ` : ''}session, tagged, so you can find the session where somebody had your problem.`
export const noTopicLine = group => `No ${group ? `${group} ` : ''}session has that topic yet. Ask it in the WhatsApp group and it'll be on the next recording.`
export const SEARCH_PLACEHOLDER = 'Search by topic, like delayed completion'
// a mastermind's mark on a session or a recording they missed live and haven't watched (the Accelerator critique's
// member 9, 20 Oct): Sessions and Recordings
export const MISSED_MARK = 'You missed this one'
export const SEARCH_LABEL = 'Search recordings by topic'
export const ASK = {
  button: 'Ask about this',
  placeholder: 'What do you want to ask? Add the minute if you know it.',
  note: 'Adam gets it with a link to this recording.',
  send: 'Send to Adam',
  sent: 'Sent. Adam has your question with a link to this recording.',
}

// The recordings released to them, newest first
function released(data) {
  const content = new Map((data?.content || []).map(c => [c.recording_id, c]))
  return (data?.recordings || [])
    .filter(r => content.has(r.id))
    .map(r => ({ r, c: content.get(r.id) }))
    .sort((a, b) => String(b.r.recorded_on || '').localeCompare(String(a.r.recorded_on || '')) || a.r.sort_order - b.r.sort_order)
}

// [{ tag, recordings }], most used first, then by name
export function topicsFrom(data) {
  const counts = new Map()
  for (const { c } of released(data)) {
    const seen = new Set()
    for (const t of Array.isArray(c.tags) ? c.tags : []) {
      const k = String(t).toLowerCase()
      if (!t || seen.has(k)) continue
      seen.add(k)
      const was = counts.get(k)
      counts.set(k, { tag: was && was.tag < t ? was.tag : t, recordings: (was?.recordings || 0) + 1 })
    }
  }
  return [...counts.values()].sort((a, b) => b.recordings - a.recordings || a.tag.localeCompare(b.tag, 'en', { sensitivity: 'base' }))
}

// "September 2026"
export function monthLabel(recordedOn) {
  if (!recordedOn) return ''
  const d = f.calendarDay(recordedOn)
  return `${MONTHS[d.m - 1]} ${d.y}`
}

// The rows to draw: [{ id, key, sessionId, title, href, block, day, length, month, tags: [{ name, on }], watched, thumb }].
// sessionId: the session it was recorded at, for a mastermind's "You missed this one" (TaggedRecordings.jsx).
// thumb (decision 47, 12 Oct): null while none of the group's recordings has Adam's own thumbnail, so
// each row keeps the board's date tile; else the 16:9 frame (the thumbnail, or the grey date panel).
export function taggedRows(data, product, { term = '', topic = '' } = {}) {
  const q = term.trim().toLowerCase()
  const t = topic.trim().toLowerCase()
  const base = product?.path || ''
  const out = []
  const any = anyThumbnail(released(data).map(x => x.r))
  for (const { r, c } of released(data)) {
    const tags = (Array.isArray(c.tags) ? c.tags : []).filter(Boolean)
    const chapters = Array.isArray(c.chapters) ? c.chapters : []
    if (t && !tags.some(x => x.toLowerCase() === t)) continue
    if (q && !(r.title || '').toLowerCase().includes(q) && !tags.some(x => x.toLowerCase().includes(q))
      && !chapters.some(ch => String(ch.title || '').toLowerCase().includes(q))) continue
    out.push({
      id: r.id,
      key: r.key,
      sessionId: r.session_id || null,
      title: r.title,
      href: r.key ? `${base}/${r.key}` : null,
      block: r.recorded_on ? f.dateBlock(f.calendarDay(r.recorded_on)) : null,
      // the date in words, beside the length where the row's frame shows a picture (decision 47)
      day: r.recorded_on ? f.shortDate(f.calendarDay(r.recorded_on)) : null,
      length: c.duration_seconds ? f.duration(c.duration_seconds) : '',
      month: monthLabel(r.recorded_on),
      tags: tags.map(name => ({ name, on: Boolean((t && name.toLowerCase() === t) || (q && name.toLowerCase().includes(q))) })),
      watched: Boolean(progressFor(data, r.id)?.watched_at),
      // every row here is open to them (released: its content came back)
      thumb: cardThumb(r, { any, sessions: data?.sessions, open: true }),
    })
  }
  return out
}

// The rows in months, newest first: [{ month, rows }]
export function byMonth(rows) {
  const out = []
  for (const row of rows) {
    const last = out[out.length - 1]
    if (last && last.month === row.month) last.rows.push(row)
    else out.push({ month: row.month, rows: [row] })
  }
  return out
}
